
'use client';

import { useState } from 'react';
import { toPng } from 'html-to-image';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Trip, TripDataPoint } from "@/lib/types";
import { Car, Timer, Waypoints, Gauge, History, Trash2, TimerOff, ParkingCircle, Zap, Flag, Map, Pin, Share2, Milestone, Database, HardDrive, Loader2, Copy } from "lucide-react";
import { TripShareCard } from './trip-share-card';
import { useToast } from '@/hooks/use-toast';
import { renderToStaticMarkup } from 'react-dom/server';
import { haversineDistance } from '@/lib/utils';


const formatDuration = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return { value: '--', unit: undefined };
    const totalSeconds = Math.round(seconds);
    if (totalSeconds < 60) {
        return { value: totalSeconds.toString(), unit: 's' };
    }
    if (totalSeconds < 3600) {
        return { value: (totalSeconds / 60).toFixed(1), unit: 'min' };
    }
    const hours = totalSeconds / 3600;
    return { value: hours.toFixed(2), unit: 'hr' };
};

const getTripSizeInBytes = (trip: Trip): number => {
  if (typeof window === 'undefined' || !trip) return 0;
  try {
    return new Blob([JSON.stringify(trip)]).size;
  } catch {
    return 0;
  }
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};


const StatCard = ({ icon: Icon, title, value, unit, description, className }: { icon: React.ElementType, title: string, value: string, unit?: string, description?: string, className?: string }) => (
  <div className={`flex items-center gap-3 p-2 rounded-lg bg-background ${className}`}>
    <div className={`p-2.5 rounded-full ${className ? '' : 'bg-primary/10 text-primary'}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className="text-xl font-bold">{value}</p>
      {unit && <p className={`text-xs -mt-1 ${className ? '' : 'text-muted-foreground'}`}>{unit}</p>}
      {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
    </div>
  </div>
);


const generateMapsUrl = (significantEvents: TripDataPoint[] | undefined, fullTripData: TripDataPoint[] | undefined): string => {
  const allPoints = (significantEvents && significantEvents.length > 0) ? significantEvents : fullTripData;
  if (!allPoints || allPoints.length < 1) return '';
  
  const validPoints = allPoints.filter(p => p.lat !== null && p.lon !== null);
  if (validPoints.length < 1) return '';

  const origin = validPoints[0];
  const destination = validPoints.length > 1 ? validPoints[validPoints.length - 1] : origin;
  
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lon}&destination=${destination.lat},${destination.lon}&travelmode=driving`;
  
  // Get stop events (all except start and end)
  let stopEvents = validPoints.slice(1, -1);

  const MAX_WAYPOINTS = 8; // Google Maps has a limit, typically around 23, but we'll be conservative and smart.
  
  if (stopEvents.length > MAX_WAYPOINTS) {
    // If we have too many stops, we need to be smart about which ones we show.
    // This is a simple strategy: take the most significant stops based on distance between them.
    // A better strategy might consider stop duration, but this is a good start.
    
    let filteredStops: TripDataPoint[] = [];
    if (stopEvents.length > 0) {
      filteredStops.push(stopEvents[0]); // Always include the first stop

      for (let i = 1; i < stopEvents.length; i++) {
        const lastIncludedStop = filteredStops[filteredStops.length - 1];
        const currentStop = stopEvents[i];
        
        const distance = haversineDistance(
          { lat: lastIncludedStop.lat!, lon: lastIncludedStop.lon! },
          { lat: currentStop.lat!, lon: currentStop.lon! }
        );
        
        // This threshold can be adjusted. e.g., 0.5km.
        // It ensures we don't add waypoints for stops that are very close together (e.g. adjusting parking)
        if (distance > 0.2) {
          filteredStops.push(currentStop);
        }
      }
    }
    stopEvents = filteredStops;

    // If we still have too many, we need to be more aggressive.
    // A more complex strategy would be to prioritize stops that are furthest apart.
    // For now, we'll just take a sample.
    if (stopEvents.length > MAX_WAYPOINTS) {
        const step = Math.ceil(stopEvents.length / MAX_WAYPOINTS);
        stopEvents = stopEvents.filter((_, i) => i % step === 0);
    }

    stopEvents = stopEvents.slice(0, MAX_WAYPOINTS);
  }


  if (stopEvents.length > 0) {
    const waypointsStr = stopEvents
      .map(p => `${p.lat},${p.lon}`)
      .join('|');
    url += `&waypoints=${waypointsStr}`;
  }

  return url;
}

const generatePointMapUrl = (point: TripDataPoint | undefined): string => {
  if (!point || point.lat === null || point.lon === null) return '';
  return `https://www.google.com/maps/search/?api=1&query=${point.lat},${point.lon}`;
};


type TripHistoryProps = {
  trips: Trip[];
  onClearHistory: () => void;
  onDeleteTrip: (tripId: string) => void;
};

export function TripHistory({ trips, onClearHistory, onDeleteTrip }: TripHistoryProps) {
  const [open, setOpen] = useState(false);
  const [sharingTripId, setSharingTripId] = useState<string | null>(null);
  const { toast } = useToast();
  
  const handleShareClick = async (trip: Trip) => {
      setSharingTripId(trip.id);
      
      try {
          const container = document.createElement('div');
          container.style.position = 'fixed';
          container.style.top = '-200vh';
          container.style.left = '0';
          
          const staticMarkup = renderToStaticMarkup(<TripShareCard trip={trip} />);
          container.innerHTML = staticMarkup;
          
          document.body.appendChild(container);
          
          const nodeToCapture = container.firstChild as HTMLElement;
          if (!nodeToCapture) throw new Error("Failed to create node for image capture.");

          const dataUrl = await toPng(nodeToCapture, { cacheBust: true, pixelRatio: 2 });
          
          document.body.removeChild(container);

          const blob = await (await fetch(dataUrl)).blob();
          
          await navigator.clipboard.write([
            new ClipboardItem({
                'image/png': blob
            })
          ]);

          toast({
            title: 'Image Copied!',
            description: 'Trip summary copied. You can now paste it in any app.'
          });

      } catch (err: any) {
          console.error('Sharing failed:', err);
          let description = 'Could not copy the trip image.';
          if (err.name === 'NotAllowedError') {
            description = 'Clipboard permission was denied.';
          } else if (err instanceof TypeError) {
             description = 'Could not copy image. Clipboard API may not be supported on this browser.'
          }
          toast({ variant: 'destructive', title: 'Share Failed', description });
      } finally {
        setSharingTripId(null);
      }
  };
  
  const totalSize = trips.reduce((sum, trip) => sum + getTripSizeInBytes(trip), 0);
  const formattedTotalSize = formatSize(totalSize);

  const DeleteTripButton = ({ tripId }: { tripId: string }) => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Trash2 className="h-4 w-4" />
          <span className="sr-only">Delete Trip</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this trip?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete this trip from your history.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onDeleteTrip(tripId)}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  
  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="outline">
            <History className="mr-2 h-4 w-4" />
            Trip History
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader>
            <SheetTitle>Trip History</SheetTitle>
            <SheetDescription className="flex items-center gap-2">
              Review your saved trips.
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground/80 font-mono bg-muted px-2 py-0.5 rounded-full">
                <HardDrive className="w-3 h-3" />
                {formattedTotalSize}
              </span>
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="flex-grow my-4 -mx-6 px-6">
            {trips.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                <History className="w-16 h-16 mb-4" />
                <p className="text-lg font-semibold">No Trips Saved</p>
                <p className="text-sm">Complete a trip to see it here.</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {trips.map((trip) => {
                  const durationDisplay = formatDuration(trip.duration);
                  const totalStopTimeDisplay = formatDuration(trip.totalStopTime);
                  const timeTo100Display = formatDuration(trip.timeTo100);
                  const oneKmResultTimeDisplay = formatDuration(trip.oneKmResult?.time);
                  const fullTripMapsUrl = generateMapsUrl(trip.significantEvents, trip.tripData);
                  const distanceDisplayValue = trip.distance < 1 ? trip.distance.toFixed(2) : trip.distance.toFixed(1);
                  const tripSize = formatSize(getTripSizeInBytes(trip));

                  const allPoints = trip.significantEvents || trip.tripData || [];
                  const firstPoint = allPoints.length > 0 ? allPoints[0] : null;
                  const lastPoint = allPoints.length > 0 ? allPoints[allPoints.length - 1] : null;
                  
                  const startPointUrl = generatePointMapUrl(firstPoint ?? undefined);
                  const endPointUrl = generatePointMapUrl(lastPoint ?? undefined);
                  const isSharing = sharingTripId === trip.id;

                  return (
                    <AccordionItem key={trip.id} value={trip.id}>
                      <AccordionTrigger>
                        <div className="flex items-center gap-4 w-full">
                          <Car className="w-5 h-5 text-primary flex-shrink-0" />
                          <div className="flex-grow text-left">
                            <p className="font-semibold">Trip</p>
                            <p className="text-xs text-muted-foreground">{new Date(trip.date).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground pr-2">
                             <Database className="w-3.5 h-3.5" />
                             <span>{tripSize}</span>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-2">
                        <div className="space-y-2 text-xs text-muted-foreground p-3 rounded-lg bg-background">
                              {firstPoint && (
                                <div className="flex items-start">
                                  <Pin className="w-3.5 h-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
                                  <span className="font-semibold">Start:</span>&nbsp;
                                  <a href={startPointUrl} target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-primary">
                                    {new Date(firstPoint.timestamp).toLocaleTimeString()} ({firstPoint.lat?.toFixed(4)}, {firstPoint.lon?.toFixed(4)})
                                  </a>
                                </div>
                              )}
                              
                              {trip.speedCheckResults && trip.speedCheckResults.map(check => {
                                const avgSpeed = check.duration > 0 ? (check.distance / (check.duration / 3600)) : 0;
                                return (
                                <div key={check.id} className="pl-5 pt-2 pb-1">
                                    <div className="flex items-center p-2 rounded-md border border-dashed border-primary/50 bg-primary/10">
                                      <Milestone className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-primary" />
                                      <span className="font-semibold text-primary">Avg. Speed Check:</span>&nbsp;
                                      <span className="text-primary/90">{avgSpeed.toFixed(1)} km/h over {check.distance.toFixed(2)} km</span>
                                    </div>
                                </div>
                              )})}

                              {lastPoint && firstPoint?.timestamp !== lastPoint.timestamp && (
                                <div className="flex items-start">
                                  <Flag className="w-3.5 h-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
                                  <span className="font-semibold">End:</span>&nbsp;&nbsp;&nbsp;
                                  <a href={endPointUrl} target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-primary">
                                    {new Date(lastPoint.timestamp).toLocaleTimeString()} ({lastPoint.lat?.toFixed(4)}, {lastPoint.lon?.toFixed(4)})
                                  </a>
                                </div>
                              )}
                          </div>

                        <div className="grid grid-cols-2 gap-4">
                            <StatCard icon={Timer} title="Duration" value={durationDisplay.value} unit={durationDisplay.unit} />
                            <StatCard icon={Waypoints} title="Distance" value={distanceDisplayValue} unit="km" />
                            <StatCard icon={Gauge} title="Avg Speed" value={trip.avgSpeed.toFixed(0)} unit="km/h" className="text-yellow-400 bg-yellow-400/10" />
                            <StatCard icon={Gauge} title="Max Speed" value={trip.maxSpeed.toFixed(0)} unit="km/h" className="text-red-500 bg-red-500/10" />
                            <StatCard icon={ParkingCircle} title="Stops" value={(trip.stopCount ?? 0).toString()} />
                            <StatCard icon={TimerOff} title="Time Stopped" value={totalStopTimeDisplay.value} unit={totalStopTimeDisplay.unit} />
                            <StatCard icon={Zap} title="0-100 km/h" value={timeTo100Display.value} unit={timeTo100Display.unit} />
                            <StatCard icon={Flag} title="1km Time" value={oneKmResultTimeDisplay.value} unit={oneKmResultTimeDisplay.unit} description={trip.oneKmResult ? `Max: ${trip.oneKmResult.maxSpeed.toFixed(0)} km/h` : ' '} />
                        </div>
                        
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                           {fullTripMapsUrl ? (
                            <Button asChild variant="outline">
                              <a href={fullTripMapsUrl} target="_blank" rel="noopener noreferrer" className="w-full flex justify-center">
                                <Map className="mr-2 h-4 w-4" />
                                Map
                              </a>
                            </Button>
                          ) : <div />}
                           
                           <DeleteTripButton tripId={trip.id} />

                           <Button variant="outline" onClick={() => handleShareClick(trip)} disabled={isSharing} className="w-full">
                              {isSharing ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Copying...
                                </>
                              ) : (
                                <>
                                  <Copy className="mr-2 h-4" />
                                  Copy Image
                                </>
                              )}
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )
                })}
              </Accordion>
            )}
          </ScrollArea>
          <SheetFooter>
            {trips.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Clear History
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete all your trip history.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => {
                      onClearHistory();
                      setOpen(false);
                    }}>
                      Continue
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

    

    